"use client";

import {
  createContext,
  use,
  useState,
  useCallback,
  useMemo,
  lazy,
  Suspense,
} from "react";
import type { AccountType } from "@/lib/auth/types";
import type { Id } from "@convex/_generated/dataModel";

const AIChatDialog = lazy(() =>
  import("@/components/support/AIChatDialog").then((m) => ({
    default: m.AIChatDialog,
  })),
);

export type SearchContextType = "public" | "dashboard" | "admin";

interface SearchContextValue {
  isOpen: boolean;
  openSearch: () => void;
  closeSearch: () => void;

  query: string;
  setQuery: (q: string) => void;

  context: SearchContextType;
  companyId?: Id<"companies">;
  setCompanyId: (id: Id<"companies"> | undefined) => void;
  accountType: AccountType | undefined;

  openAIChat: (prefillQuery: string) => void;
  isAIChatOpen: boolean;
  closeAIChat: () => void;

  openPublicChat: (message: string) => void;
  publicChatMessage: string | null;
  clearPublicChatMessage: () => void;
}

const SearchContext = createContext<SearchContextValue | null>(null);

export function useSearchContext() {
  const ctx = use(SearchContext);
  if (!ctx) {
    throw new Error("useSearchContext must be used within a SearchProvider");
  }
  return ctx;
}

const SSR_FALLBACK: SearchContextValue = {
  isOpen: false,
  openSearch: () => {},
  closeSearch: () => {},
  query: "",
  setQuery: () => {},
  context: "public",
  companyId: undefined,
  setCompanyId: () => {},
  accountType: undefined,
  openAIChat: () => {},
  isAIChatOpen: false,
  closeAIChat: () => {},
  openPublicChat: () => {},
  publicChatMessage: null,
  clearPublicChatMessage: () => {},
};

export function useSearchContextSafe(): SearchContextValue {
  const ctx = use(SearchContext);
  return ctx ?? SSR_FALLBACK;
}

export interface SearchProviderProps {
  children: React.ReactNode;
  context?: SearchContextType;
  defaultCompanyId?: Id<"companies">;
  defaultAccountType?: AccountType;
}

export function SearchProvider({
  children,
  context = "public",
  defaultCompanyId,
  defaultAccountType,
}: SearchProviderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [companyId, setCompanyId] = useState<Id<"companies"> | undefined>(
    defaultCompanyId,
  );
  const [accountType] = useState<AccountType | undefined>(defaultAccountType);
  const [isAIChatOpen, setIsAIChatOpen] = useState(false);
  const [aiChatPrompt, setAIChatPrompt] = useState<string | null>(null);
  const [publicChatMessage, setPublicChatMessage] = useState<string | null>(
    null,
  );

  const openSearch = useCallback(() => setIsOpen(true), []);
  const closeSearch = useCallback(() => setIsOpen(false), []);

  const openAIChat = useCallback((prefillQuery: string) => {
    setIsOpen(false);
    setAIChatPrompt(prefillQuery);
    setIsAIChatOpen(true);
  }, []);

  const closeAIChat = useCallback(() => {
    setIsAIChatOpen(false);
    setAIChatPrompt(null);
  }, []);

  const openPublicChat = useCallback((message: string) => {
    setPublicChatMessage(message);
  }, []);

  const clearPublicChatMessage = useCallback(() => {
    setPublicChatMessage(null);
  }, []);

  const value = useMemo<SearchContextValue>(
    () => ({
      isOpen,
      openSearch,
      closeSearch,
      query,
      setQuery,
      context,
      companyId,
      setCompanyId,
      accountType,
      openAIChat,
      isAIChatOpen,
      closeAIChat,
      openPublicChat,
      publicChatMessage,
      clearPublicChatMessage,
    }),
    [
      isOpen,
      openSearch,
      closeSearch,
      query,
      context,
      companyId,
      accountType,
      openAIChat,
      isAIChatOpen,
      closeAIChat,
      openPublicChat,
      publicChatMessage,
      clearPublicChatMessage,
    ],
  );

  return (
    <SearchContext value={value}>
      {children}

      {isAIChatOpen && (
        <Suspense fallback={<div />}>
          <AIChatDialog
            isOpen
            onClose={closeAIChat}
            initialPrompt={aiChatPrompt}
          />
        </Suspense>
      )}
    </SearchContext>
  );
}
